import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/content/site";
import { MouseScrubPortrait } from "@/components/about/MouseScrubPortrait";
import sunsetRoad from "@/assets/tamal-sunset-road.jpg?w=600;900;1200&format=webp&as=srcset";
import sunsetRoadFallback from "@/assets/tamal-sunset-road.jpg?w=1200&format=webp";
import sunsetField from "@/assets/tamal-sunset-mustard-field.jpg?w=600;900;1200&format=webp&as=srcset";
import sunsetFieldFallback from "@/assets/tamal-sunset-mustard-field.jpg?w=1200&format=webp";
import { ArrowUpRight } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { buildSeo } from "@/lib/seo";
import { buildAboutGraph } from "@/lib/about-schema";
import {
  PILLARS,
  WHY,
  SKILLS,
  ABOUT_SERVICES,
  ABOUT_FAQ,
  APPROACH,
  ABOUT_REVIEWED_ON,
  AI_SEARCH_POINTS,
} from "@/content/about-content";
import { AiSearchCards } from "@/components/about/AiSearchCards";
import { FaqCardGrid } from "@/components/FaqCardGrid";
import {
  AboutContactCard,
  AboutServiceGrid,
  ApproachCards,
  PillarGrid,
  SkillsList,
  WhyChecklist,
} from "@/components/about/AboutSections";


export const Route = createFileRoute("/about")({
  head: () => {
    const seo = buildSeo({
      title: "About Freelancer Tamal — Senior SEO & AEO Expert, Rangpur",
      description:
        "Senior SEO, AEO, GEO & digital marketing expert in Rangpur, Bangladesh covering technical SEO, local SEO, Shopify SEO and AEO/GEO for AI search.",
      path: "/about",
      type: "profile",
      alternates: { bn: "/bn/about" },
      keywords:
        "about Freelancer Tamal, SEO expert Rangpur, AEO expert Bangladesh, GEO consultant, best digital marketing expert Bangladesh, freelance digital marketing consultant, technical SEO expert, local SEO Rangpur, Shopify SEO expert",
    });
    return {
    meta: [
      ...seo.meta,
      { name: "geo.region", content: "BD-55" },
      { name: "geo.placename", content: "Rangpur, Bangladesh" },
      { name: "geo.position", content: "25.7439;89.2752" },
      { name: "ICBM", content: "25.7439, 89.2752" },
      { property: "profile:first_name", content: "Tamal" },
      { property: "profile:username", content: "freelancertamal" },
    ],
    links: seo.links,
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildAboutGraph(ABOUT_REVIEWED_ON)),
      },
    ],
  };
  },
  component: About,
});

function About() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]} />
      {/* HERO */}
      <Section eyebrow="About Freelancer Tamal">
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] -mt-10 mb-12 max-w-4xl">
          Senior SEO expert in <span className="text-gradient">Rangpur, Bangladesh</span> — technical SEO, content and AI search.
        </h1>
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          <Reveal className="lg:col-span-3">
            <p className="text-xl text-muted-foreground leading-relaxed">
              Meet <strong className="text-foreground">Freelancer Tamal</strong> — an SEO consultant in {SITE.city}, {SITE.country}.
              I offer search engine optimization services
              built around your business. Local SEO. Technical SEO. Ethical, measurable results.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90">
                Contact Today <ArrowUpRight size={16} />
              </Link>
              <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-card">
                See Services
              </Link>
            </div>
          </Reveal>
          <div className="lg:col-span-2">
            <MouseScrubPortrait />
          </div>
        </div>
      </Section>

      {/* PILLARS */}
      <Section eyebrow="What I bring" title={<>Four pillars of <span className="text-gradient">my practice.</span></>}>
        <PillarGrid items={PILLARS} headingLevel="h3" />
      </Section>

      {/* WHY CHOOSE */}
      <Section eyebrow="Why choose" title={<>Freelancer Tamal as your <span className="text-gradient">SEO expert?</span></>}>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <p className="text-lg text-muted-foreground leading-relaxed">
              My consulting focuses on technical SEO, local search, ecommerce and AI-search readiness.
              Priorities come from your site's actual problems and customer questions.
              Performance claims need dated, property-specific evidence.
            </p>
          </Reveal>
          <WhyChecklist items={WHY} />
        </div>
      </Section>

      {/* MEET / CONTACT CARD */}
      <Section eyebrow="Meet" title={<>An SEO consultant <span className="text-gradient">in Rangpur.</span></>}>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <p>
                Based in Rangpur, I offer technical SEO, local search and content planning.
                The approach depends on the business, its audience and the evidence available —
                not a guaranteed ranking or a fixed success timeline.
              </p>
              <p className="text-foreground italic">
                "I think quality is non-negotiable. If a client doesn't love the work, I don't either — and I
                won't move forward like that."
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <AboutContactCard
              facts={[
                { label: "Focus", value: "SEO & AI search" },
                { label: "Based in", value: "Rangpur, BD" },
              ]}
            />
          </Reveal>
        </div>
      </Section>

      {/* SKILLS BARS */}
      <Section eyebrow="Background" title={<>My journey in <span className="text-gradient">digital marketing.</span></>}>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div className="space-y-5 text-muted-foreground leading-relaxed">
              <p>
                A lifelong fascination with technology and a love for working with people pulled me into digital
                marketing. Learning SEO and the tools around it, I
                quickly saw how dramatically they could change a business's trajectory.
              </p>
              <p>
                Project after project, I sharpened my skills in local SEO, keyword research, and technical SEO. Today,
                I use that compounded experience to help clients climb search results, reach more buyers, and grow
                organic revenue sustainably.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <SkillsList skills={SKILLS} />
          </Reveal>
        </div>
      </Section>

      {/* OFF-SCREEN / PERSONAL */}
      <Section eyebrow="Off the clock" title={<>Rangpur, on <span className="text-gradient">golden hour.</span></>}>
        <div className="grid md:grid-cols-2 gap-6">
          <Reveal>
            <figure className="rounded-3xl overflow-hidden border border-border aspect-[4/5] relative group">
              <img
                src={sunsetRoadFallback}
                srcSet={sunsetRoad}
                sizes="(min-width: 768px) 50vw, 100vw"
                alt="Freelancer Tamal walking at golden hour on a quiet rural road through eucalyptus trees outside Rangpur, Bangladesh"
                className="w-full h-full object-cover transition duration-700 group-hover:scale-[1.03]"
                loading="lazy"
                decoding="async"
                width={1200}
                height={1500}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/0 to-transparent" />
              <figcaption className="absolute bottom-6 left-6 right-6 text-sm text-foreground/90">
                Sunset walks outside the city — where most of my clearest strategy ideas show up.
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.08}>
            <figure className="rounded-3xl overflow-hidden border border-border aspect-[4/5] relative group">
              <img
                src={sunsetFieldFallback}
                srcSet={sunsetField}
                sizes="(min-width: 768px) 50vw, 100vw"
                alt="Freelancer Tamal standing in a yellow mustard-flower field at sunset in Rangpur Division, Bangladesh"
                className="w-full h-full object-cover transition duration-700 group-hover:scale-[1.03]"
                loading="lazy"
                decoding="async"
                width={1200}
                height={1500}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/0 to-transparent" />
              <figcaption className="absolute bottom-6 left-6 right-6 text-sm text-foreground/90">
                Mustard fields in winter — a reminder that the best work compounds quietly, like a good SEO program.
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </Section>

      {/* SERVICES GRID */}
      <Section eyebrow="Services" title={<>Comprehensive SEO, <span className="text-gradient">end to end.</span></>}>
        <AboutServiceGrid services={ABOUT_SERVICES} headingLevel="h3" />
      </Section>

      {/* AEO & GEO */}
      <Section eyebrow="AI search" title={<>AEO &amp; GEO for <span className="text-gradient">AI search.</span></>}>
        <AiSearchCards
          items={AI_SEARCH_POINTS}
          intro="Search now happens in Google AI Overviews, ChatGPT, Perplexity and Gemini as well as the classic results page. Alongside SEO, I help businesses become a clear, citable answer in those tools."
          more="Learn more"
        />
      </Section>

      {/* PROCESS / NARRATIVE */}
      <Section eyebrow="My approach" title={<>How my SEO process <span className="text-gradient">drives real results.</span></>}>
        <ApproachCards items={APPROACH} headingLevel="h3" />
      </Section>

      {/* FAQ */}
      <Section eyebrow="FAQ" title={<>Frequently asked <span className="text-gradient">questions.</span></>}>
        <FaqCardGrid items={ABOUT_FAQ} />
      </Section>

      {/* CTA */}
      <Section className="!pt-0">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-primary/15 via-card to-card p-12 lg:p-16 text-center">
          <h2 className="font-display text-3xl md:text-5xl leading-tight">Ready to improve your search visibility?</h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">Let's talk about your site, your competitors, and the keywords that actually move revenue.</p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 text-sm font-medium hover:opacity-90">
            Start a conversation <ArrowUpRight size={16} />
          </Link>
        </div>
      </Section>
    </>
  );
}
